import { useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { ApiError } from '../api/client'
import { postRefund, postVoid, type HistoryItem } from './api'
import { detailsErrorText } from './details'
import { todayIso } from './endDate'
import { dollars, parseCents, refundableCents, refundValid, voidValid, type RefundMembership } from './member'

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'
const inp = `min-h-[40px] rounded-lg border border-ink-400 bg-ink-800 px-3 text-paper ${focus}`
const lbl = 'flex flex-col gap-1.5 text-[13px] font-semibold text-paper/70'
const btn = `inline-flex min-h-[40px] items-center rounded-lg border border-ink-400 px-4 text-[13px] font-semibold text-paper hover:bg-ink-600 ${focus}`

export type MoneyAction = 'void' | 'refund'

const MEMBERSHIP_CHOICES: { value: RefundMembership; label: string; sub: string }[] = [
  { value: 'end_now', label: 'End it now', sub: 'Access stops today and auto-renew stops.' },
  { value: 'keep', label: 'Keep the time they have', sub: 'Access runs to the current end date. A full refund still stops auto-renew.' },
  { value: 'new_end', label: 'Set a new end date', sub: 'Access runs to the date you pick.' },
]

/** An in-page confirmation for voiding or refunding one payment from the history. */
export function MoneyDialog({ action, item, onClose, onDone }: {
  action: MoneyAction
  item: HistoryItem
  onClose: () => void
  onDone: (message: string) => void
}) {
  const paid = item.amount_cents ?? 0
  // Prefilled with, and capped at, what is left after earlier refunds.
  const left = refundableCents(item)
  const offline = !item.refund_via_gateway
  const [amount, setAmount] = useState((left / 100).toFixed(2))
  const [reference, setReference] = useState('')
  const [reason, setReason] = useState('')
  const [membership, setMembership] = useState<RefundMembership | ''>('')
  const [newEnd, setNewEnd] = useState('')
  const today = todayIso()
  const [error, setError] = useState<string | null>(null)
  const busy = useRef(false)

  const run = useMutation({
    mutationFn: () => {
      const orderKey = item.order_key as string
      return action === 'void'
        ? postVoid({ order_key: orderKey, reason: reason.trim() })
        : postRefund({
            order_key: orderKey, amount_cents: parseCents(amount), reason: reason.trim(),
            ...(offline ? { refund_reference: reference.trim() } : {}),
            membership: membership as RefundMembership,
            ...(membership === 'new_end' ? { new_end: newEnd } : {}),
          })
    },
    onSuccess: () => onDone(action === 'void' ? 'Payment voided.' : `Refunded ${dollars(parseCents(amount))}.`),
    onError: (e) => setError(e instanceof ApiError ? detailsErrorText(e.status, e.detail) : 'Could not reach the server. Nothing was changed. Check your connection and try again.'),
    onSettled: () => { busy.current = false },
  })

  const ready = action === 'void'
    ? voidValid({ reason })
    : refundValid({ amountCents: parseCents(amount), maxCents: left, reference, method: offline ? 'offline' : 'card', reason, membership, newEnd, today })

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!ready || busy.current) return
    busy.current = true
    setError(null)
    run.mutate()
  }

  const title = action === 'void' ? 'Void this payment' : 'Refund this payment'
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onKeyDown={(e) => { if (e.key === 'Escape' && !run.isPending) onClose() }}>
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="money-h"
        onSubmit={submit}
        className="flex max-h-full w-full max-w-md flex-col gap-4 overflow-y-auto rounded-xl border border-ink-500 bg-ink-700 p-5"
      >
        <h2 id="money-h" className="font-serif text-[20px]">{title}</h2>
        <p className="m-0 text-paper/70">{item.what} of <strong className="text-paper">{dollars(paid)}</strong>{item.detail ? ` · ${item.detail}` : ''}</p>
        {action === 'void' ? (
          <p className="m-0 text-paper/70">The time this payment added will be taken back off the membership.</p>
        ) : (
          <>
            {left < paid && <p className="m-0 text-paper/70">{dollars(paid - left)} has already been refunded; up to {dollars(left)} is left.</p>}
            <label className={lbl}>
              Amount to refund
              <input className={inp} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
            </label>
            {offline && (
              <label className={lbl}>
                Our refund check or money order number
                <input className={inp} value={reference} onChange={(e) => setReference(e.target.value)} />
              </label>
            )}
            <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
              <legend className="mb-1.5 text-[13px] font-semibold text-paper/70">What happens to the membership? (required)</legend>
              {MEMBERSHIP_CHOICES.map((c) => (
                <label key={c.value} className={`flex cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2.5 ${membership === c.value ? 'border-amber-glow bg-amber-wash' : 'border-ink-400'}`}>
                  <input type="radio" name="refund-membership" className="mt-1" value={c.value} checked={membership === c.value} onChange={() => setMembership(c.value)} />
                  <span className="flex flex-col">
                    <span className="text-[14px] font-bold">{c.label}</span>
                    <span className="text-[12px] text-paper/60">{c.sub}</span>
                  </span>
                </label>
              ))}
              {membership === 'new_end' && (
                <label className={lbl}>
                  New end date (after today)
                  <input className={inp} type="date" min={nextDay(today)} value={newEnd} onChange={(e) => setNewEnd(e.target.value)} />
                </label>
              )}
            </fieldset>
          </>
        )}
        <label className={lbl}>
          Reason (required)
          <textarea className={`${inp} py-2`} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus={action === 'void'} />
        </label>
        {error && <p role="alert" className="m-0 text-red-500">{error}</p>}
        <div className="flex flex-wrap gap-2">
          <button type="submit" disabled={!ready || run.isPending} className={`inline-flex min-h-[40px] items-center rounded-lg bg-paper px-4 font-bold text-ink-900 disabled:opacity-50 ${focus}`}>
            {run.isPending ? 'Working…' : action === 'void' ? 'Void payment' : 'Refund'}
          </button>
          <button type="button" className={btn} onClick={onClose} disabled={run.isPending}>Cancel</button>
        </div>
      </form>
    </div>
  )
}

/** The day after a YYYY-MM-DD date, for the date picker's minimum. */
function nextDay(iso: string): string {
  const d = new Date(`${iso}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + 1)
  return d.toISOString().slice(0, 10)
}
